import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api, type DataProviderLimits, type FairUseWindow } from "../../../api/client.ts";
import { keys } from "../../../api/queryKeys.ts";
import { formatNumber } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import Sparkline from "../../Sparkline.tsx";
import { StatusLabel } from "../../StatusIcon.tsx";

const numeric = { fontVariantNumeric: "tabular-nums" } as const;

// Share of a limit at which a meter turns amber, then red.
const WARN_AT = 0.8;
const CRITICAL_AT = 0.95;

type Level = "primary" | "warning" | "error";

function level(share: number): Level {
  if (share >= CRITICAL_AT) return "error";
  if (share >= WARN_AT) return "warning";
  return "primary";
}

function percent(share: number): string {
  if (share > 0 && share < 0.01) return "<1%";
  return `${Math.round(share * 100)}%`;
}

/**
 * How close the platform's GetLeads account runs to its fair-use limits: rows
 * pulled today and this month against the caps (GetLeads' own count) and
 * requests per minute against our budget and the account's limit (our count).
 */
export default function GetLeadsFairUsePanel() {
  // Polls instead of `meta: { live: true }`: the rows used are counted at
  // GetLeads, so no write of ours announces them. The server shares one
  // answer a minute, so polling faster would show nothing new.
  const limits = useQuery({
    queryKey: keys.platform.dataProviderLimits(),
    queryFn: () => api.getDataProviderLimits(),
    refetchInterval: 60_000,
  });
  const data = limits.data;
  if (!data) return null;

  return (
    <Panel title={`${data.provider_label} fair use`} subtitle={subtitle(data)}>
      {data.error && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {data.configured ? `${data.provider_label} didn't answer: ${data.error}` : data.error}
        </Typography>
      )}
      <Grid container spacing={3}>
        {data.applies === false ? (
          <Grid size={{ xs: 12, md: 8 }}>
            <Meter
              label="Credits left"
              value={data.credits_remaining == null ? "—" : formatNumber(data.credits_remaining)}
              caption="This key is on a plan billed in credits; the row caps apply to unlimited plans."
            />
          </Grid>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 4 }}>
              <RowsMeter
                label="Rows today"
                window={data.daily}
                binding={data.binding === "daily"}
                resets="Resets 00:00 UTC"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <RowsMeter
                label="Rows this month"
                window={data.monthly}
                binding={data.binding === "monthly"}
                resets="Resets on the 1st, 00:00 UTC"
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, md: 4 }}>
          <RequestsMeter data={data} />
        </Grid>
      </Grid>
    </Panel>
  );
}

function subtitle(data: DataProviderLimits): string {
  const plan = data.plan ? `${data.plan[0].toUpperCase()}${data.plan.slice(1)} plan` : "Plan unknown";
  return `${plan} · rows pulled by search, export and enrichment, and requests per minute`;
}

function Meter({
  label,
  value,
  caption,
  share,
  color = "primary",
  children,
}: {
  label: string;
  value: ReactNode;
  caption: ReactNode;
  share?: number;
  color?: Level;
  children?: ReactNode;
}) {
  return (
    <Stack spacing={0.75}>
      <Typography variant="overline" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={numeric}>
        {value}
      </Typography>
      {share != null && (
        <LinearProgress
          variant="determinate"
          color={color}
          value={Math.min(100, share * 100)}
          sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover" }}
        />
      )}
      {children}
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {caption}
      </Typography>
    </Stack>
  );
}

function RowsMeter({
  label,
  window,
  binding,
  resets,
}: {
  label: string;
  window: FairUseWindow | null;
  binding: boolean;
  resets: string;
}) {
  if (!window || window.cap == null || window.used == null) {
    return <Meter label={label} value="—" caption={resets} />;
  }
  const share = window.cap > 0 ? window.used / window.cap : 0;
  return (
    <Meter
      label={label}
      value={
        <>
          {formatNumber(window.used)}
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {" "}
            / {formatNumber(window.cap)}
          </Box>
        </>
      }
      share={share}
      color={level(share)}
      caption={`${percent(share)} used${binding ? " · the limit reached first" : ""} · ${resets}`}
    />
  );
}

function RequestsMeter({ data }: { data: DataProviderLimits }) {
  const perMinute = data.requests_per_minute;
  const limited = data.rate_limited_per_minute.reduce((n, v) => n + v, 0);
  if (!perMinute.length) {
    return (
      <Meter
        label="Requests per minute"
        value="—"
        caption={`Counters unavailable · budget ${data.rpm_budget}, account limit ${data.rpm_account}`}
      />
    );
  }
  const peak = Math.max(...perMinute);
  const total = perMinute.reduce((n, v) => n + v, 0);
  const share = data.rpm_budget > 0 ? peak / data.rpm_budget : 0;
  return (
    <Meter
      label="Requests per minute · peak last hour"
      value={
        <>
          {formatNumber(peak)}
          <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
            {" "}
            / {formatNumber(data.rpm_budget)}
          </Box>
        </>
      }
      caption={`Our budget ${formatNumber(data.rpm_budget)} of the account's ${formatNumber(data.rpm_account)} · ${formatNumber(total)} requests in the last hour`}
    >
      <Tooltip title={`Requests sent each minute of the last hour, now on the right; full height is our budget (peak ${percent(share)} of it)`}>
        <Box component="span" sx={{ display: "inline-flex" }}>
          <Sparkline values={perMinute} ceiling={data.rpm_budget} width={180} height={28} showRange={false} />
        </Box>
      </Tooltip>
      {limited > 0 ? (
        <StatusLabel kind="attention" label={`${formatNumber(limited)} rate-limited (429) in the last hour`} />
      ) : (
        <StatusLabel kind="ok" label="No rate-limit errors in the last hour" />
      )}
    </Meter>
  );
}
